import React, { useState, useRef, useEffect } from 'react';

interface PhoneInputProps {
  name: string;
  value?: string;
  country?: string;
  countryCode?: string;  // Custom country code for "Other" option
  label?: string;
  placeholder?: string;
  required?: boolean;
  onChange?: (value: string, country: string) => void;
  disableCountrySelect?: boolean;
  hasError?: boolean;
}

// Country configurations
const countries = [
  { code: 'US', flag: '🇺🇸', name: 'United States', dialCode: '+1' },
  { code: 'CA', flag: '🇨🇦', name: 'Canada', dialCode: '+1' },
  { code: 'GB', flag: '🇬🇧', name: 'United Kingdom', dialCode: '+44' },
  { code: 'OTHER', flag: '🌍', name: 'Other', dialCode: '+' },
];

export const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  value = '',
  country = 'US',
  countryCode = '',
  label = 'Cell Phone',
  placeholder = 'Enter your cell phone number',
  required = true,
  onChange,
  disableCountrySelect = false,
  hasError = false,
}) => {
  // Determine initial country selection based on countryCode prop
  const getInitialCountry = () => {
    if (countryCode && countryCode.startsWith('+')) {
      // Check if countryCode matches a predefined country's dial code
      const matchingCountry = countries.find(c => c.dialCode === countryCode && c.code !== 'OTHER');
      if (matchingCountry) {
        return matchingCountry;
      }
      // If countryCode doesn't match predefined, use "Other"
      return countries.find(c => c.code === 'OTHER')!;
    }
    // Fall back to country prop for backwards compatibility
    return countries.find(c => c.code === country) || countries[0];
  };

  const initialCountry = getInitialCountry();
  const shouldSelectOther = initialCountry.code === 'OTHER';
  // Track if we selected based on countryCode prop (should not respond to country prop changes)
  const selectedFromCountryCode = !!(countryCode && countryCode.startsWith('+'));

  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // For "Other" option, phoneNumber holds only digits (no +)
  // Strip + from value if loading "Other" option
  const [phoneNumber, setPhoneNumber] = useState(
    shouldSelectOther && value ? value.replace(/\+/g, '') : value
  );
  const [customDialCode, setCustomDialCode] = useState(shouldSelectOther ? countryCode : '');
  const [isOtherSelected, setIsOtherSelected] = useState(shouldSelectOther);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debug: Log on mount
  useEffect(() => {
    if (shouldSelectOther) {
      console.log('🌍 PhoneInput loaded with "Other" option:', {
        value: value,
        countryCode: countryCode,
        displayNumber: phoneNumber
      });
    }
  }, []);

  // Update selected country when country prop changes (only if not selected from countryCode)
  useEffect(() => {
    // Skip if we already determined the country from countryCode prop
    if (selectedFromCountryCode) return;

    const newCountry = countries.find(c => c.code === country);
    if (newCountry) {
      setSelectedCountry(newCountry);
    }
  }, [country, selectedFromCountryCode]);

  // Listen for country changes from external sources (like country dropdown in Location section)
  useEffect(() => {
    if (disableCountrySelect) {
      const handleCountryChange = (event: CustomEvent) => {
        const newCountryCode = event.detail.country;
        const newCountry = countries.find(c => c.code === newCountryCode);
        if (newCountry) {
          setSelectedCountry(newCountry);
        }
      };

      window.addEventListener('country-changed' as any, handleCountryChange as EventListener);
      return () => {
        window.removeEventListener('country-changed' as any, handleCountryChange as EventListener);
      };
    }
  }, [disableCountrySelect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [localError, setLocalError] = React.useState(false);

  const handleCountrySelect = (countryCode: string) => {
    const newCountry = countries.find(c => c.code === countryCode);
    if (newCountry) {
      setSelectedCountry(newCountry);
      setIsDropdownOpen(false);
      setLocalError(false);

      if (countryCode === 'OTHER') {
        setIsOtherSelected(true);
        // Clear phone number when switching to "Other"
        setPhoneNumber('');
        setCustomDialCode('');
      } else {
        setIsOtherSelected(false);
        // If switching from "Other" to regular country, strip the + from phone number
        if (isOtherSelected && phoneNumber.startsWith('+')) {
          setPhoneNumber(phoneNumber.substring(1));
        }
      }

      if (onChange) {
        onChange(phoneNumber, newCountry.code);
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isOtherSelected) {
      // For "Other +", user types only digits (no +)
      // We prepend + automatically when saving
      const digitsOnly = e.target.value.replace(/\D/g, '');

      // Store only digits in phoneNumber state (for display)
      setPhoneNumber(digitsOnly);

      // Smart extraction of country code from digits
      // Common patterns:
      // 1 (US/CA): 1 digit code + 10 digit number = 11 total
      // 52 (Mexico), 44 (UK), 91 (India): 2 digit code + 10 digit number = 12 total
      // 971 (UAE), 966 (Saudi): 3 digit code + 8-9 digit number = 11-12 total
      if (digitsOnly.length > 0) {
        let codeLength = 2; // Default to 2 digits (most common worldwide)

        // Heuristic based on total digit count AND first digit
        if (digitsOnly.length === 11) {
          // 11 total could be:
          // - 1-digit code (US/CA): +1 with 10-digit number
          // - 3-digit code (UAE): +971 with 8-digit number
          // Check first digit to decide
          if (digitsOnly.startsWith('1')) {
            codeLength = 1; // Likely US/CA
          } else {
            // Try 3-digit code for countries like UAE (+971), Saudi (+966)
            codeLength = 3;
          }
        } else if (digitsOnly.length === 12) {
          // 12 total could be:
          // - 2-digit code with 10-digit number (most common: +52, +44, +91)
          // - 3-digit code with 9-digit number (less common)
          // Default to 2-digit
          codeLength = 2;
        } else if (digitsOnly.length === 13) {
          // 13 total = likely 3-digit code with 10-digit number
          codeLength = 3;
        } else if (digitsOnly.length >= 14) {
          // 14+ total = might be 3 or 4-digit code
          // Try 3 digits
          codeLength = 3;
        } else if (digitsOnly.length === 10) {
          // 10 total = 1-digit code with 9-digit number (rare)
          codeLength = 1;
        } else if (digitsOnly.length < 10) {
          // Less than 10: default to 2 digits
          codeLength = Math.min(2, digitsOnly.length);
        }

        // Extract country code (with +)
        const extractedCode = '+' + digitsOnly.substring(0, codeLength);
        setCustomDialCode(extractedCode);

        // Console log for debugging
        console.log('🌍 Phone Input Debug:', {
          input: digitsOnly,
          totalDigits: digitsOnly.length,
          extractedCountryCode: extractedCode,
          remainingNumber: digitsOnly.substring(codeLength),
          fullNumberWithPlus: '+' + digitsOnly
        });
      } else {
        setCustomDialCode('');
      }

      // Validate length for "Other" - minimal length check (e.g. 5)
      // If user clears input, we don't show error unless specific validation cycle triggers it
      if (digitsOnly.length > 0 && digitsOnly.length < 5) {
        setLocalError(true);
      } else {
        setLocalError(false);
      }

      if (onChange) {
        // Send with + prepended for storage
        onChange(digitsOnly ? '+' + digitsOnly : '', selectedCountry.code);
      }
    } else {
      // Normal handling for predefined countries with length limits
      // US/CA: 10, UK: 11
      const maxLength = selectedCountry.code === 'GB' ? 11 : 10;
      const newValue = e.target.value.replace(/\D/g, '').slice(0, maxLength);
      setPhoneNumber(newValue);

      // Validate length for standard 10-digit countries (US/CA etc)
      if (newValue.length > 0 && newValue.length < 10) {
        setLocalError(true);
      } else {
        setLocalError(false);
      }

      if (onChange) {
        onChange(newValue, selectedCountry.code);
      }
    }
  };

  return (
    <div className="w-full">
      {/* Always send country_code to persist phone country selection */}
      <input
        type="hidden"
        name={name.replace(/\[.*\]$/, '[country_code]')}
        value={isOtherSelected ? customDialCode : selectedCountry.dialCode}
      />
      {/* For "Other", we need to send the phone with + prepended */}
      {isOtherSelected && phoneNumber && (
        <input
          type="hidden"
          name={name}
          value={'+' + phoneNumber}
        />
      )}

      {/* Frame 457 - Outer Container */}
      <div
        className="flex flex-row items-start"
        style={{
          boxSizing: 'border-box',
          padding: '4px',
          backgroundColor: 'rgba(233, 238, 246, 0.5)',
          border: (hasError || localError) ? '1px solid #dc2626' : '1px solid rgba(140, 162, 194, 0.5)',
          borderRadius: '10px',
          minHeight: '62px'
        }}
      >
        {/* Inline Country Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex flex-row items-center cursor-pointer transition-colors"
            style={{
              padding: '6px 8px 6px 12px',
              gap: '4px',
              background: '#E9EEF6',
              borderRadius: '6px',
              border: 'none',
              height: '54px'
            }}
          >
            <span
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                textAlign: 'center',
                letterSpacing: '-0.005em',
                color: '#576C89'
              }}
            >
              {selectedCountry.dialCode}
            </span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#374151"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div
              className="absolute left-0 top-full mt-1 bg-white shadow-lg z-50"
              style={{
                minWidth: '220px',
                borderRadius: '6px',
                border: '1px solid rgba(140, 162, 194, 0.5)'
              }}
            >
              {countries.map((countryOption) => (
                <button
                  key={countryOption.code}
                  type="button"
                  onClick={() => handleCountrySelect(countryOption.code)}
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-100 transition-colors"
                  style={{
                    gap: '8px',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                >
                  <span className="text-left">{countryOption.name} ({countryOption.dialCode})</span>
                  {/* Tick mark for selected country - on the right */}
                  {selectedCountry.code === countryOption.code && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#374151" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Frame 458 - Label and Input Container */}
        <div
          className="flex flex-row items-center flex-1"
          style={{
            padding: '6px 12px',
            gap: '10px',
            height: '54px'
          }}
        >
          {/* Frame 327 - Label and Input */}
          <div
            className="flex flex-col items-start flex-1"
            style={{ padding: '0px', gap: '4px' }}
          >
            {/* Inline Label */}
            <label
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                fontSize: '11px',
                lineHeight: '16px',
                letterSpacing: '0.005em',
                color: '#374151'
              }}
            >
              {label}{required && '*'}
            </label>

            {/* Frame 312 - Input Row */}
            <div
              className="flex flex-row items-center w-full"
              style={{ padding: '0px', gap: '6px' }}
            >
              {/* Phone Input */}
              <input
                type="text"
                name={isOtherSelected ? undefined : name}
                value={phoneNumber}
                onChange={handlePhoneChange}
                onInvalid={() => setLocalError(true)}
                placeholder={isOtherSelected ? 'Enter your phone number with country code' : placeholder}
                required={required}
                maxLength={isOtherSelected ? undefined : (selectedCountry.code === 'GB' ? 11 : 10)}
                pattern="[0-9]*"
                inputMode="numeric"
                onKeyPress={(e) => {
                  // Only allow digits (no + needed, it's in the dropdown)
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  if (isOtherSelected) {
                    // Strip all non-digits including + from pasted data, no length limit
                    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
                    handlePhoneChange({ target: { value: pastedData } } as React.ChangeEvent<HTMLInputElement>);
                  } else {
                    // US/CA: 10, UK: 11
                    const maxLength = selectedCountry.code === 'GB' ? 11 : 10;
                    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, maxLength);
                    setPhoneNumber(pastedData);
                    if (onChange) {
                      onChange(pastedData, selectedCountry.code);
                    }
                  }
                }}
                className="flex-1 bg-transparent outline-none focus:ring-0 focus:border-0 border-0"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '150%',
                  letterSpacing: '-0.005em',
                  color: '#111827',
                  padding: 0,
                  margin: 0
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneInput;
